/**
 * ActionButton Component
 *
 * Renders a single action button based on ActionDefinition.
 * Handles clicks, confirmations, haptic feedback.
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { ActionDefinition, ActionSize } from "../types/actions";
import { EmojiText } from './low/AnimatedEmoji'

interface ActionButtonProps {
  action: ActionDefinition;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
}

export const ActionButton: React.FC<ActionButtonProps> = ({
  action,
  onClick,
  loading = false,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const getVariantClasses = () => {
    switch (action.variant) {
      case "primary":
        return "bg-primary text-primary-foreground border-pixel-outline hover:bg-primary-hover";
      case "secondary":
        return "bg-secondary text-secondary-foreground border-pixel-outline hover:opacity-90";
      case "danger":
        return "bg-destructive text-destructive-foreground border-pixel-outline hover:opacity-90";
      case "ghost":
        return "bg-transparent border-border text-muted-foreground hover:bg-accent";
      default:
        return "bg-primary text-primary-foreground border-pixel-outline hover:bg-primary-hover";
    }
  };

  // Typography follows `size`. The touch-target minimum height stays regardless
  // of padding — a button small enough to mis-tap is a bug in every layout, and
  // `padding` is about spacing, not about shrinking the tap area.
  const getSizeClasses = () => {
    switch (action.size) {
      case "sm":
        return "text-xs min-h-touch";
      case "md":
        return "text-sm min-h-touch";
      case "lg":
        return "text-base font-semibold min-h-touch";
      case "xl":
        return "text-lg font-bold min-h-touch";
      default:
        return "text-base font-semibold min-h-touch";
    }
  };

  // Spacing: `padding` overrides the padding implied by `size` when set, using
  // exactly the same scale — `padding="md"` gives the padding of `size="md"`.
  // All class names are written out literally: Tailwind's JIT cannot generate
  // class names composed at runtime.
  const paddingForScale = (scale: ActionSize | undefined) => {
    switch (scale) {
      case "sm":
        return "px-4 py-2";
      case "md":
        return "px-6 py-3";
      case "lg":
        return "px-8 py-4";
      case "xl":
        return "px-10 py-5";
      default:
        return "px-8 py-4";
    }
  };

  const getPaddingClasses = () => {
    if (action.padding === "none") return "";
    if (action.padding) return paddingForScale(action.padding);
    // Undefined size falls through to the same default as getSizeClasses.
    return paddingForScale(action.size);
  };

  return (
    <button
      onClick={onClick}
      disabled={action.disabled || loading || disabled}
      title={action.disabled_reason || action.description}
      data-testid={`action-button-${action.id}`}
      data-action-id={action.id}
      data-action-label={action.label}
      className={`
        ${getPaddingClasses()} ${getSizeClasses()} ${getVariantClasses()}
        relative overflow-hidden rounded-none border-2 font-display
        pixel-shadow transition-none pixel-press
        active:brightness-125
        disabled:opacity-50 disabled:cursor-not-allowed
        ${loading ? "animate-pulse" : ""}
      `}
    >
      {/* min-w-0 + break-words: in a dense grid the label must wrap onto another
          line instead of being cut off by the button's overflow-hidden. */}
      <div className="flex items-center justify-center relative z-10 min-w-0">
        {action.icon && <span className="mr-2 text-xl"><EmojiText>{action.icon}</EmojiText></span>}
        <span className="min-w-0 break-words">{loading ? t("common.processing") : action.label}</span>
      </div>
    </button>
  );
};
